import {
  type FileSnapshot,
  type InventoryResource,
  SecurityScanConclusionEnum,
  SecurityScanDetectorTypeEnum,
  SecurityScanResultEvidenceTypeEnum,
  type Snapshot,
} from '@repo/api-gw-sdk';

// Returns true only for ransomware conclusions (not Malware or DATA_ANOMALY).
// Use this where the skull icon / "ransomware indicators" copy should appear.
export const hasRansomwareIssue = (entity: InventoryResource): boolean => {
  if ('securityScans' in entity && entity.securityScans?.length) {
    return entity.securityScans.some(
      (scan) =>
        scan.conclusion === SecurityScanConclusionEnum.HighChanceForRansomware
    );
  }
  return 'isInfected' in entity && !!entity.isInfected;
};

// Returns true when the resource has any DATA_ANOMALY scan (clean or not),
// so the anomaly card (including the "No Anomaly Detected" clean state) is always rendered.
export const hasDataAnomalyScan = (entity: InventoryResource): boolean => {
  return (
    entity.securityScans?.some(
      (scan) => scan.detectorType === SecurityScanDetectorTypeEnum.DataAnomaly
    ) ?? false
  );
};

export const hasSecurityIssue = (
  entity: InventoryResource | Snapshot | FileSnapshot
) => {
  if ('securityScans' in entity && entity.securityScans?.length) {
    return entity.securityScans.some(
      (scan) =>
        scan.conclusion && scan.conclusion !== SecurityScanConclusionEnum.Clean
    );
  }
  if ('securityScan' in entity && entity.securityScan?.conclusion) {
    return entity.securityScan.conclusion !== SecurityScanConclusionEnum.Clean;
  }
  return 'isInfected' in entity && !!entity.isInfected;
};

const EvidenceTypeDisplayNameMapper: Record<
  SecurityScanResultEvidenceTypeEnum,
  { label: string; entity: string; description: string }
> = {
  [SecurityScanResultEvidenceTypeEnum.SuspiciousExtensionDetected]: {
    label: 'Ransomware File Signature',
    entity: 'file',
    description: 'found with suspicious signatures',
  },
  [SecurityScanResultEvidenceTypeEnum.SuspiciousActivityDetected]: {
    label: 'Moved files',
    entity: 'file',
    description: 'moved and encrypted',
  },
  [SecurityScanResultEvidenceTypeEnum.ModifiedFilesBecameEncrypted]: {
    label: 'File entropy',
    entity: 'file',
    description: 'found with suspicious entropy',
  },
  [SecurityScanResultEvidenceTypeEnum.MalwareDetected]: {
    label: 'Malware Detected',
    entity: 'file',
    description: 'detected with malware signature',
  },
  [SecurityScanResultEvidenceTypeEnum.TableDeletion]: {
    label: 'Table Deletion',
    entity: 'table',
    description: 'deleted',
  },
  [SecurityScanResultEvidenceTypeEnum.SignificantRowDecrease]: {
    label: 'Significant Row Decrease',
    entity: 'table',
    description: 'detected with significant decrease in row count',
  },
  [SecurityScanResultEvidenceTypeEnum.HighChangePercentage]: {
    label: 'High Change Percentage',
    entity: 'table',
    description: 'unusually high percentage of data changes detected',
  },
  [SecurityScanResultEvidenceTypeEnum.KmsUnavailableResources]: {
    label: 'KMS Encryption Detected',
    entity: 'file',
    description: 'encrypted with inaccessible KMS/SSE-C key',
  },
  [SecurityScanResultEvidenceTypeEnum.DbRansomNoteTableName]: {
    label: 'DB Ransom Note',
    entity: 'table',
    description: 'detected with a ransom note table name',
  },
  [SecurityScanResultEvidenceTypeEnum.DbRansomNoteContent]: {
    label: 'DB Ransom Note',
    entity: 'table',
    description: 'detected with ransom note content',
  },
  [SecurityScanResultEvidenceTypeEnum.MassFileDeleteAnomaly]: {
    label: 'Mass File Deletion',
    entity: 'resource',
    description: 'anomalous decrease in file count detected',
  },
};

export const getEvidenceStrings = (
  evidenceType: SecurityScanResultEvidenceTypeEnum
): { label: string; description: string; entity: string } => {
  return (
    EvidenceTypeDisplayNameMapper[evidenceType] ?? {
      label: evidenceType,
      description: 'detected',
      entity: 'item',
    }
  );
};
